import React, { useEffect, useState, useCallback } from 'react';
import {
  AnalyticalTable,
  Button,
  Dialog,
  Title,
  BusyIndicator,
  Input,
  Bar,
  Form,
  FormItem,
  FormGroup,
  MessageBox,
  MessageBoxType,
  MessageBoxAction,
  Text,
  Icon,
  TextArea,
  Label,
  Select,
  Option
} from '@ui5/webcomponents-react';
import axiosInstance from '../utils/axiosInstance';
import usePermission from '../hooks/usePermission';
import withPermission from './withPermission';
import useAutoTranslate from '../hooks/useAutoTranslate';
import Swal from 'sweetalert2';

const companyTypes = [
  { value: 'MANDANTE', label: 'Mandante' },
  { value: 'PROVEEDOR', label: 'Proveedor' }
];

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true
});

const CompanyList = () => {
  const { hasPermission } = usePermission();
  const { autoT } = useAutoTranslate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    sortBy: 'name',
    sortDirection: 'asc'
  });
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    fullName: '',
    templateAsn: '',
    templatePurchase: '',
    companyType: '',
    companyEmail: '',
    taxNumber: '',
    districts: []
  });
  const [formErrors, setFormErrors] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const fetchCompanies = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const url = `companies?page=${page}&size=${pageSize}`;
      const response = await axiosInstance.get(url);
      if (response.data.records.code === "SUCCESS") {
        setData(response.data.records.data);
        setTotalPages(Math.ceil(response.data.totalCount / pageSize));
      } else {
        setError(response.data.records.message);
        Toast.fire({ icon: 'error', title: response.data.records.message || autoT('Error al cargar las compañías') });
      }
    } catch (err) {
      setError('Error al cargar las compañías: ' + (err.message || 'Error desconocido'));
      Toast.fire({ icon: 'error', title: autoT('Error al cargar las compañías') });
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  useEffect(() => {
    fetchCompanies(currentPage);
  }, [currentPage, fetchCompanies]);

  useEffect(() => {
    let filtered = [...data];
    
    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(company => 
        company.name?.toLowerCase().includes(lowerCaseSearchTerm) ||
        company.fullName?.toLowerCase().includes(lowerCaseSearchTerm) ||
        company.taxNumber?.toLowerCase().includes(lowerCaseSearchTerm) ||
        company.companyEmail?.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }
    
    filtered.sort((a, b) => {
      const valueA = a[sortConfig.sortBy];
      const valueB = b[sortConfig.sortBy];
      
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sortConfig.sortDirection === 'asc' 
          ? valueA.localeCompare(valueB) 
          : valueB.localeCompare(valueA);
      } else {
        return sortConfig.sortDirection === 'asc' 
          ? valueA - valueB 
          : valueB - valueA;
      }
    });
    
    setFilteredData(filtered);
  }, [data, searchTerm, sortConfig]);

  const handleSort = (column) => {
    setSortConfig(prevConfig => ({
      sortBy: column,
      sortDirection: prevConfig.sortBy === column && prevConfig.sortDirection === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      fullName: '',
      templateAsn: '',
      templatePurchase: '',
      companyType: '',
      companyEmail: '',
      taxNumber: '',
      districts: []
    });
    setFormErrors({});
  };

  const handleRowClick = (event) => {
    if (!hasPermission('companies', 'update')) {
      return;
    }
    
    const { detail } = event;
    if (detail && detail.row && detail.row.original) {
      const company = detail.row.original;
      setSelectedCompany(company);
      setFormData({
        name: company.name || '',
        fullName: company.fullName || '',
        templateAsn: company.templateAsn || '',
        templatePurchase: company.templatePurchase || '',
        companyType: company.companyType || '',
        companyEmail: company.companyEmail || '',
        taxNumber: company.taxNumber || '',
        districts: company.districts || []
      });
      setFormErrors({});
      setIsEditing(true);
      setShowDialog(true);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleEditClick = (company) => {
    if (!hasPermission('companies', 'update')) {
      return;
    }
    
    setSelectedCompany(company);
    setFormData({
      name: company.name || '',
      fullName: company.fullName || '',
      templateAsn: company.templateAsn || '',
      templatePurchase: company.templatePurchase || '',
      companyType: company.companyType || '0',
      companyEmail: company.companyEmail || '',
      taxNumber: company.taxNumber || '',
      districts: company.districts || []
    });
    setFormErrors({});
    setIsEditing(true);
    setShowDialog(true);
  };

  const handleCreateClick = () => {
    if (!hasPermission('companies', 'create')) {
      return;
    }
    
    resetForm();
    setSelectedCompany(null);
    setIsEditing(false);
    setShowDialog(true);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSelectChange = (event) => {
    const { name } = event.target;
    const value = event.detail.selectedOption.getAttribute('value');
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name) {
      errors.name = 'El nombre es obligatorio';
    }
    if (!formData.fullName) {
      errors.fullName = 'El nombre completo es obligatorio';
    }
    if (!formData.companyType) {
      errors.companyType = 'El tipo de compañía es obligatorio';
    }
    if (!formData.taxNumber) {
      errors.taxNumber = 'El RUT/Tax Number es obligatorio';
    }
    if (formData.companyEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.companyEmail)) {
      errors.companyEmail = 'El formato del email no es válido';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      let response;
      if (isEditing) {
        const payload = {
          ...formData,
          id: selectedCompany.id,
          tenantId: selectedCompany.tenantId || JSON.parse(localStorage.getItem('userData') || '{}')?.tenantId
        };
        response = await axiosInstance.put(`companies/${selectedCompany.id}`, payload);
      } else {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        const payload = {
          ...formData,
          tenantId: userData.tenantId || null,
          active: true
        };
        response = await axiosInstance.post('companies', payload);
      }
      
      if (response.status === 200 || response.status === 201) {
        fetchCompanies(currentPage);
        setShowDialog(false);
        Toast.fire({
          icon: 'success',
          title: isEditing ? autoT('Compañía actualizada exitosamente') : autoT('Compañía creada exitosamente')
        });
      } else {
        const action = isEditing ? 'actualizar' : 'crear';
        const msg = response.data?.message || response.data?.data || 'Error desconocido';
        setError(`Error al ${action} la compañía: ${msg}`);
        Toast.fire({ icon: 'error', title: msg });
      }
    } catch (err) {
      const action = isEditing ? 'actualizar' : 'crear';
      const msg = err.response?.data?.message || err.response?.data?.data || err.message || 'Error desconocido';
      setError(`Error al ${action} la compañía: ${msg}`);
      Toast.fire({ icon: 'error', title: msg });
    }
  };

  const handleDeleteClick = () => {
    if (!hasPermission('companies', 'delete')) {
      return;
    }
    
    if (selectedCompany) {
      setShowDeleteConfirm(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedCompany) return;
    
    try {
      await axiosInstance.delete(`companies/${selectedCompany.id}`);
      fetchCompanies(currentPage);
      setShowDialog(false);
      setShowDeleteConfirm(false);
      Toast.fire({ icon: 'success', title: autoT('Compañía eliminada exitosamente') });
    } catch (err) {
      setError('Error al eliminar la compañía: ' + (err.message || 'Error desconocido'));
      Toast.fire({ icon: 'error', title: autoT('Error al eliminar la compañía') });
    }
  };

  const columns = [
    {
      Header: autoT('Nombre'),
      accessor: 'name',
      headerProps: {
        onClick: () => handleSort('name')
      },
      className: sortConfig.sortBy === 'name' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: autoT('Nombre Completo'),
      accessor: 'fullName',
      headerProps: {
        onClick: () => handleSort('fullName')
      },
      className: sortConfig.sortBy === 'fullName' ? `sorted-${sortConfig.sortDirection}` : ''
    },
//    {
//        Header: autoT('Tipo'),
//        accessor: 'companyType',
 //       Cell: ({ value }) => (
 //         <div style={{ display: 'flex', alignItems: 'center' }}>
//            <Icon 
//            name={value === 'MANDANTE' ? "building" : "supplier"} 
//            style={{ marginRight: '8px', color: value === 'MANDANTE' ? '#0070f3' : '#28a745' }} 
//          />
//          <Text>{companyTypes.find(type => type.value === value)?.label || value}</Text>
//        </div>
//      ),
//      headerProps: {
//        onClick: () => handleSort('companyType')
//      },
//      className: sortConfig.sortBy === 'companyType' ? `sorted-${sortConfig.sortDirection}` : ''
//    },
    {
      Header: autoT('RUT'),
      accessor: 'taxNumber',
      headerProps: {
        onClick: () => handleSort('taxNumber')
      },
      className: sortConfig.sortBy === 'taxNumber' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: autoT('Email'),
      accessor: 'companyEmail',
      Cell: ({ value }) => (
        <Text style={{ 
          fontSize: '0.95rem',
          color: value ? '#2c3e50' : '#95a5a6',
          fontWeight: '500'
        }}>
          {value || autoT('No especificado')}
        </Text>
      )
    },
    {
      Header: autoT('Acciones'),
      id: 'actions',
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          {hasPermission('companies', 'update') && (
            <Button 
              icon="edit" 
              design="Transparent" 
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(row.original);
              }}
            />
          )}
        </div>
      )
    }
  ];

  return (
    <div style={{
      padding: '1.5rem',
      backgroundColor: 'var(--sapBackgroundColor)',
      minHeight: '100vh'
    }}>
      <div style={{ padding: '1rem' }}>
        <Title level="H2">{autoT('Gestión de Compañías')}</Title>
        
        {error && (
          <div style={{ color: 'red', margin: '1rem 0' }}>
            {error}
          </div>
        )}
        
        <Bar
          startContent={
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <Input
                placeholder={autoT('Buscar compañía...')}
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ width: '300px' }}
              />
            </div>
          }
          endContent={
            hasPermission('companies', 'create') && (
              <Button 
                icon="add" 
                design="Emphasized"
                onClick={handleCreateClick}
              >
                {autoT('Nueva Compañía')}
              </Button>
            )
          }
        />
        
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <BusyIndicator active size="Large" />
          </div>
        ) : (
          <>
            <AnalyticalTable
              data={filteredData}
              columns={columns}
              selectionMode="None"
              onRowClick={hasPermission('companies', 'update') ? handleRowClick : undefined}
              style={{ marginTop: '1rem' }}
              visibleRows={10}
              minRows={10}
              alternateRowColor
            />
            
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1rem' }}>
              <Button 
                icon="navigation-left-arrow" 
                disabled={currentPage === 1}
                onClick={handlePrevPage}
              >
                Anterior
              </Button>
              <Text style={{ alignSelf: 'center' }}>
                Página {currentPage} de {totalPages}
              </Text>
              <Button 
                icon="navigation-right-arrow" 
                disabled={currentPage === totalPages}
                onClick={handleNextPage}
                iconEnd
              >
                Siguiente
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Dialog Unificado para Crear/Editar Compañía */}
      <Dialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        header={
          <Bar
            startContent={
              <Title level="H4">
                {isEditing ? `${autoT('Editar Compañía')}: ${selectedCompany?.name}` : autoT('Crear Nueva Compañía')}
              </Title>
            }
          />
        }
        footer={
          <Bar
            startContent={
              hasPermission('companies', 'delete') && isEditing && (
                <Button design="Negative" onClick={handleDeleteClick} icon="delete">
                  {autoT('Eliminar')}
                </Button>
              )
            }
            endContent={
              <>
                <Button 
                  design="Emphasized" 
                  onClick={handleSubmit}
                >
                  {isEditing ? autoT('Guardar Cambios') : autoT('Crear Compañía')}
                </Button>
                <Button 
                  design="Transparent" 
                  onClick={() => setShowDialog(false)}
                >
                  {autoT('Cancelar')}
                </Button>
              </>
            }
          />
        }
        style={{ width: '900px', maxHeight: '90vh' }}
      >
        <Form style={{ padding: '1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {/* Columna 1 - Información Básica */}
            <FormGroup titleText="Información Básica">
              <FormItem labelContent={<Label>Nombre</Label>}>
                <Input 
                  name="name" 
                  value={formData.name}
                  onChange={handleInputChange}
                  valueState={formErrors.name ? "Error" : "None"}
                  valueStateMessage={formErrors.name}
                  required
                />
              </FormItem>
              <FormItem labelContent={<Label>Nombre Completo</Label>}>
                <Input 
                  name="fullName" 
                  value={formData.fullName}
                  onChange={handleInputChange}
                  valueState={formErrors.fullName ? "Error" : "None"}
                  valueStateMessage={formErrors.fullName}
                  required
                />
              </FormItem>
              <FormItem labelContent={<Label>RUT/Tax Number</Label>}>
                <Input
                  name="taxNumber"
                  value={formData.taxNumber}
                  onChange={handleInputChange}
                  valueState={formErrors.taxNumber ? "Error" : "None"}
                  valueStateMessage={formErrors.taxNumber}
                  placeholder="Ingrese RUT o número de identificación fiscal"
                  required
                />
              </FormItem>
              <FormItem labelContent={<Label>Tipo de Compañía</Label>}>
                <Select
                  name="companyType"
                  onChange={handleSelectChange}
                  valueState={formErrors.companyType ? "Error" : "None"}
                >
                  <Option value="">Seleccione un tipo</Option>
                  <Option value="MANDANTE" selected={formData.companyType === 'MANDANTE'}>Mandante</Option>
                  <Option value="PROVEEDOR" selected={formData.companyType === 'PROVEEDOR'}>Proveedor</Option>
                </Select>
                {formErrors.companyType && (
                  <Text style={{ color: 'var(--sapNegativeTextColor)', fontSize: '0.75rem' }}>
                    {formErrors.companyType}
                  </Text>
                )}
              </FormItem>
            </FormGroup>

            {/* Columna 2 - Información de Contacto */}
            <FormGroup titleText="Información de Contacto">
              <FormItem labelContent={<Label>Email de Compañía</Label>}>
                <Input 
                  name="companyEmail" 
                  type="email"
                  value={formData.companyEmail}
                  onChange={handleInputChange}
                  valueState={formErrors.companyEmail ? "Error" : "None"}
                  valueStateMessage={formErrors.companyEmail}
                  placeholder="empresa@ejemplo.com"
                />
              </FormItem>
            </FormGroup>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
            {/* Columna 3 - Template ASN */}
            <FormGroup titleText="Template ASN">
              <FormItem labelContent={<Label>Contenido del Template ASN</Label>}>
                <TextArea 
                  name="templateAsn" 
                  value={formData.templateAsn}
                  onChange={handleInputChange}
                  rows={8}
                  style={{ 
                    width: '100%', 
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    resize: 'vertical'
                  }}
                  placeholder="Ingrese el template ASN en formato JSON o XML..."
                />
              </FormItem>
            </FormGroup>

            {/* Columna 4 - Template Purchase */}
            <FormGroup titleText="Template Purchase">
              <FormItem labelContent={<Label>Contenido del Template Purchase</Label>}>
                <TextArea 
                  name="templatePurchase" 
                  value={formData.templatePurchase}
                  onChange={handleInputChange}
                  rows={8}
                  style={{ 
                    width: '100%', 
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    resize: 'vertical'
                  }}
                  placeholder="Ingrese el template Purchase en formato JSON o XML..."
                />
              </FormItem>
            </FormGroup>
          </div>

          {/* Sección de Distritos */}
          {formData.districts && formData.districts.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <FormGroup titleText="Distritos Asociados">
                <FormItem>
                  <div style={{ display: 'grid', gap: '0.5rem' }}>
                    {formData.districts.map((district, index) => (
                      <div key={index} style={{ 
                        padding: '1rem', 
                        border: '1px solid var(--sapGroup_ContentBorderColor)', 
                        borderRadius: '0.25rem',
                        backgroundColor: 'var(--sapGroup_ContentBackground)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <Icon name="building" style={{ marginRight: '0.5rem', color: 'var(--sapAccentColor6)' }} />
                          <Text style={{ fontWeight: 'bold', fontSize: '1rem' }}>
                            {district.districtName}
                          </Text>
                        </div>
                        {district.addresses && district.addresses.map((address, addrIndex) => (
                          <div key={addrIndex} style={{ 
                            marginLeft: '1.5rem', 
                            marginTop: '0.5rem',
                            padding: '0.5rem',
                            backgroundColor: 'var(--sapBackgroundColor)',
                            borderRadius: '0.125rem'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <Icon name="map" style={{ marginRight: '0.5rem', color: 'var(--sapNeutralTextColor)' }} />
                              <Text style={{ fontSize: '0.875rem' }}>
                                {address.street}, {address.city}, {address.state}, {address.zip}, {address.country}
                              </Text>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </FormItem>
              </FormGroup>
            </div>
          )}
        </Form>
      </Dialog>

      {/* Confirmación de eliminación */}
      <MessageBox
        open={showDeleteConfirm}
        onClose={(event) => {
          const action = event?.detail?.action;
          if (
            action === MessageBoxAction.OK ||
            action === 'OK' ||
            action === MessageBoxAction.DELETE ||
            action === 'Delete'
          ) {
            handleConfirmDelete();
          } else {
            setShowDeleteConfirm(false);
          }
        }}
        title={autoT('Confirmar eliminación')}
        type={MessageBoxType.WARNING}
        actions={[MessageBoxAction.OK, MessageBoxAction.CANCEL]}
      >
        {autoT('¿Está seguro que desea eliminar la compañía')} <strong>{selectedCompany?.name}</strong>? {autoT('Esta acción no se puede deshacer.')}
      </MessageBox>
    </div>
  );
};

export default withPermission(CompanyList, 'companies', 'listView');